import { PlusOutlined, SettingOutlined } from '@ant-design/icons';
import { useMemo } from 'react';
import styled, { css } from 'styled-components';

import { CenterFlex, Flex } from '@/components/base';
import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';
import { SpanStyle } from '@/components/Typography/Typo';
import AMP_KEYS from '@/configs/amp-track-keys';
import useGlobalSearchModalAction from '@/features/GlobalSearch/hooks/useGlobalSearchModalAction';
import {
  defaultGlobalSearchConfig,
  type GLOBAL_SEARCH_VIEW,
  GlobalSearchViewConfigMap,
} from '@/features/GlobalSearch/types/searchConst';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import useModalState from '@/hooks/pure/useModalState';
import useGlobalSearchStore from '@/stores/GlobalSearchStore';
import { getAlphaColor } from '@/styles/colors/color.utils';
import { track } from '@/utils/track/track';

import AddSearchTabModal from './AddSearchTabModal';

type Props = {
  selectedTab: GLOBAL_SEARCH_VIEW;
  setSelectedTab: (tab: GLOBAL_SEARCH_VIEW) => void;
};

const SearchTab = (props: Props) => {
  const { selectedTab, setSelectedTab } = props;
  const { closeModal } = useGlobalSearchModalAction();
  const config = useGlobalSearchStore((state) => state.search_view_config);
  const wr = useWorkspaceRouter();
  const configList = useMemo(() => {
    return [...defaultGlobalSearchConfig, ...config];
  }, [config]);
  const { open, close, status } = useModalState();

  const onManageViewClick = () => {
    track(AMP_KEYS.SEARCH_MANAGE_VIEW_CLICK);
    closeModal();
    wr.push(`/settings/search`);
  };

  return (
    <Style g={16} j="space-between">
      <Flex className="config-list" f={1}>
        <div className="gradient-left" />
        <Flex className="config-list-inner" g={8} f={1}>
          {configList.map((item) => {
            const target = GlobalSearchViewConfigMap.get(item);
            return (
              <TabItem
                key={item}
                selected={selectedTab === item}
                onClick={() => {
                  setSelectedTab(item);
                  track(AMP_KEYS.SEARCH_TAB_SELECT, {
                    tab: target?.name,
                  });
                }}
              >
                {target?.name}
              </TabItem>
            );
          })}
          <TabItem selected={false} onClick={() => open()}>
            <PlusOutlined />
          </TabItem>
        </Flex>
        <div className="gradient-right" />
      </Flex>
      <Button
        variant="ghost"
        sizeType="m"
        leftIcon={<SettingOutlined />}
        onClick={onManageViewClick}
        edgeAlign="right"
      >
        Manage view
      </Button>

      <Modal status={status} closeModal={close}>
        <AddSearchTabModal />
      </Modal>
    </Style>
  );
};

const Style = styled(Flex)`
  padding-right: 32px;

  .config-list {
    position: relative;
    overflow-x: auto;
    .config-list-inner {
      position: relative;
      overflow-x: auto;
      padding-right: 32px;
      padding-left: 32px;
      &::-webkit-scrollbar-thumb {
        border: 6px solid rgba(0, 0, 0, 0);
      }
    }
    .gradient-left {
      position: absolute;
      z-index: 1;
      left: 0;
      top: 0;
      width: 48px;
      height: 100%;
      background: linear-gradient(
        to right,
        ${(props) => getAlphaColor(props.theme.colors.gray100, 1)} 0%,
        ${(props) => getAlphaColor(props.theme.colors.gray100, 1)} 30%,
        ${(props) => getAlphaColor(props.theme.colors.gray100, 0)} 60%,
        ${(props) => getAlphaColor(props.theme.colors.gray100, 0)} 100%
      );
    }
    .gradient-right {
      position: absolute;
      z-index: 1;
      right: 0;
      top: 0;
      width: 32px;
      height: 100%;
      background: linear-gradient(
        to right,
        ${(props) => getAlphaColor(props.theme.colors.gray100, 0)},
        ${(props) => getAlphaColor(props.theme.colors.gray100, 1)}
      );
    }
  }
`;

const TabItem = styled(CenterFlex)<{ selected: boolean }>`
  padding: 10px 16px;
  border-radius: 24px;
  cursor: pointer;
  background-color: ${(props) => props.theme.colors.gray100};
  border: 1px solid ${(props) => props.theme.colors.gray400};
  color: ${(props) => props.theme.colors.gray700};
  width: fit-content;
  white-space: nowrap;
  ${SpanStyle({
    type: 'label2',
    weight: 'sb',
  })};
  transition: all 0.2s;
  &:hover {
    filter: brightness(0.95);
  }
  ${({ selected }) =>
    selected &&
    css`
      border-color: ${(props) => props.theme.colors.primary100};
      color: ${(props) => props.theme.colors.primary100};
      background-color: ${(props) =>
        getAlphaColor(props.theme.colors.primary100, 0.05)};
    `}
`;

export default SearchTab;
